import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Customers from "@/pages/Customers";
import Tasks from "@/pages/Tasks";
import Inventory from "@/pages/Inventory";
import ProductDetail from "@/pages/ProductDetail";
import Documents from "@/pages/Documents";
import DocumentBuilder from "@/pages/DocumentBuilder";
import Sops from "@/pages/Sops";
import Media from "@/pages/Media";
import Performance from "@/pages/Performance";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Attendance from "@/pages/Attendance";
import Payroll from "@/pages/Payroll";
import Challans from "@/pages/Challans";
import Accounting from "@/pages/Accounting";
import TransferPlans from "@/pages/TransferPlans";
import TransferPlanDetail from "@/pages/TransferPlanDetail";
import ReorderReport from "@/pages/ReorderReport";
import StockHistory from "@/pages/StockHistory";

function Protected() {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="auth-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

const CRM_ALLOW = ["admin", "staff", "sales", "accountant"];
const CHALLAN_ALLOW = ["admin", "staff", "sales", "accountant", "stock_team", "stock_head"];

function RoleRoute({ allow, children }) {
  const { user } = useAuth();
  if (user && !allow.includes(user.role)) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10" data-testid="no-access">
        <div className="text-lg font-semibold text-slate-900">No access</div>
        <div className="text-sm text-slate-500">Your role does not have permission to view this section.</div>
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/leads" element={<RoleRoute allow={CRM_ALLOW}><Leads /></RoleRoute>} />
            <Route path="/customers" element={<RoleRoute allow={CRM_ALLOW}><Customers /></RoleRoute>} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/attendance" element={<RoleRoute allow={CRM_ALLOW}><Attendance /></RoleRoute>} />
            <Route path="/payroll" element={<RoleRoute allow={["admin", "accountant"]}><Payroll /></RoleRoute>} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/inventory/product/:id" element={<ProductDetail />} />
            <Route path="/documents" element={<RoleRoute allow={CRM_ALLOW}><Documents /></RoleRoute>} />
            <Route path="/documents/new" element={<RoleRoute allow={CRM_ALLOW}><DocumentBuilder /></RoleRoute>} />
            <Route path="/documents/:id/edit" element={<RoleRoute allow={CRM_ALLOW}><DocumentBuilder /></RoleRoute>} />
            <Route path="/sops" element={<RoleRoute allow={CRM_ALLOW}><Sops /></RoleRoute>} />
            <Route path="/media" element={<RoleRoute allow={CRM_ALLOW}><Media /></RoleRoute>} />
            <Route path="/performance" element={<RoleRoute allow={CRM_ALLOW}><Performance /></RoleRoute>} />
            <Route path="/team" element={<RoleRoute allow={["admin"]}><Team /></RoleRoute>} />
            <Route path="/challans" element={<RoleRoute allow={CHALLAN_ALLOW}><Challans /></RoleRoute>} />
            <Route path="/transfer-plans" element={<RoleRoute allow={["stock_team", "stock_head"]}><TransferPlans /></RoleRoute>} />
            <Route path="/transfer-plans/:id" element={<RoleRoute allow={["stock_team", "stock_head"]}><TransferPlanDetail /></RoleRoute>} />
            <Route path="/reorder-report" element={<RoleRoute allow={["stock_team", "stock_head"]}><ReorderReport /></RoleRoute>} />
            <Route path="/stock-history" element={<RoleRoute allow={["stock_team", "stock_head"]}><StockHistory /></RoleRoute>} />
            <Route path="/accounts/:section?/:view?" element={<RoleRoute allow={["admin", "accountant"]}><Accounting /></RoleRoute>} />
            <Route path="/accounting" element={<Navigate to="/accounts/reports/pnl" replace />} />
            <Route path="/settings" element={<RoleRoute allow={["admin"]}><Settings /></RoleRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
